import TwigBuilder from '../Library/TwigBuilder.js';

const SELECTOR_COMPONENT = '[data-component="transformations"]';

const attachTemplateEditors = component => {
    if (!component.dataset.templateContext) {
        return;
    }

    const shape  = JSON.parse(component.dataset.templateContext);
    const labels = JSON.parse(component.dataset.twigLabels);

    component.querySelectorAll('input[data-condition-value], input[data-action-value]').forEach(field => {
        if (TwigBuilder.get(field)) {
            return;
        }

        const isActionField = field.matches('[data-action-value]');

        TwigBuilder.attach(field, {
            data:             shape.context,
            labels,
            filters:          isActionField ? shape.filters.action : shape.filters.condition,
            addButtonLabel:   '{{',
            openMenuOnBraces: true,
            allowTags:        isActionField,
            fieldTypes:       shape.types,
            dateTimeFormat:   null,
        });
    });
};

const instantiate = (template, replacements) => {
    let html = template.innerHTML;

    for (const [placeholder, value] of Object.entries(replacements)) {
        html = html.replaceAll(placeholder, String(value));
    }

    const holder = document.createElement('div');

    holder.innerHTML = html;

    return holder.firstElementChild;
};

const highestIndex = indexes => 1 + Math.max(-1, ...indexes.filter(Number.isInteger));

const nextRuleIndex = component => highestIndex(
    [...component.querySelectorAll('[data-rule]')].map(rule => Number.parseInt(rule.dataset.index, 10)),
);

const nextConditionIndex = rule => highestIndex(
    [...rule.querySelectorAll('[data-condition] select[name*="[conditions]"]')].map(select => {
        const match = select.name.match(/\[conditions]\[(\d+)]/);

        return match ? Number.parseInt(match[1], 10) : NaN;
    }),
);

const toggleEmptyState = component => {
    const hasRules = component.querySelectorAll('[data-rules] [data-rule]').length > 0;

    component.querySelector('[data-empty-state]')?.toggleAttribute('hidden', hasRules);
    component.querySelector('[data-add-rule]')?.toggleAttribute('hidden', !hasRules);
};

const touchDirty = component => {
    component.dispatchEvent(new Event('change', {bubbles: true}));
};

const clearValue = input => {
    input.value = '';
    input.dispatchEvent(new Event('input', {bubbles: true}));
};

const revealFooter = component => {
    component.closest('.side-panel')?.querySelector('footer')?.removeAttribute('hidden');
};

const addRule = component => {
    const rule = instantiate(
        component.querySelector('[data-rule-template]'),
        {__RULE__: nextRuleIndex(component)},
    );

    component.querySelector('[data-rules]').append(rule);
    toggleEmptyState(component);
    revealFooter(component);
    attachTemplateEditors(component);
    touchDirty(component);
};

const addCondition = (component, button) => {
    const rule = button.closest('[data-rule]');

    const condition = instantiate(component.querySelector('[data-condition-template]'), {
        __RULE__: rule.dataset.index,
        __CONDITION__: nextConditionIndex(rule),
    });

    rule.querySelector('[data-conditions]').append(condition);
    attachTemplateEditors(component);
    touchDirty(component);
};

const initializeComponent = component => {
    attachTemplateEditors(component);

    if (component.hasAttribute('data-has-errors')) {
        touchDirty(component);
    }
};

export const initializeTransformationEditors = root => {
    if (root.matches?.(SELECTOR_COMPONENT)) {
        initializeComponent(root);
    }

    root.querySelectorAll?.(SELECTOR_COMPONENT).forEach(initializeComponent);
};

export const handleTransformationsClick = event => {
    const addRuleButton = event.target.closest('[data-add-rule], [data-add-first-rule]');

    if (addRuleButton) {
        const component = addRuleButton.closest(SELECTOR_COMPONENT);

        if (!component) {
            return false;
        }

        addRule(component);

        return true;
    }

    const component = event.target.closest(SELECTOR_COMPONENT);

    if (!component) {
        return false;
    }

    const addConditionButton = event.target.closest('[data-add-condition]');

    if (addConditionButton) {
        addCondition(component, addConditionButton);

        return true;
    }

    const removeRule = event.target.closest('[data-remove-rule]');

    if (removeRule) {
        removeRule.closest('[data-rule]').remove();
        toggleEmptyState(component);
        touchDirty(component);

        return true;
    }

    const removeCondition = event.target.closest('[data-remove-condition]');

    if (removeCondition) {
        removeCondition.closest('[data-condition]').remove();
        touchDirty(component);

        return true;
    }

    return false;
};

export const handleTransformationsChange = event => {
    if (!(event.target instanceof HTMLSelectElement) || !event.target.closest(SELECTOR_COMPONENT)) {
        return;
    }

    if (event.target.closest('[data-operator]')) {
        const requiresValue = event.target.selectedOptions[0]?.dataset.requiresValue === '1';
        const cell          = event.target.closest('[data-condition]').querySelector('[data-value-cell]');

        cell.toggleAttribute('hidden', !requiresValue);

        if (!requiresValue) {
            clearValue(cell.querySelector('[data-condition-value]'));
        }

        return;
    }

    if (event.target.closest('[data-action-type]')) {
        const option      = event.target.selectedOptions[0];
        const needsColumn = option?.dataset.needsColumn === '1';
        const needsValue  = option?.dataset.needsValue === '1';
        const action      = event.target.closest('.transformation-action');
        const cell        = action.querySelector('[data-value-cell]');

        action.querySelector('[data-action-column]').toggleAttribute('hidden', !needsColumn);
        cell.toggleAttribute('hidden', !needsValue);

        if (!needsValue) {
            clearValue(cell.querySelector('[data-action-value]'));
        }
    }
};

document.addEventListener('sidepanel:beforeOpen', event => {
    const component = event.target.querySelector(SELECTOR_COMPONENT);

    if (!component) {
        return;
    }

    event.target.querySelector('footer')
        ?.toggleAttribute('hidden', component.querySelectorAll('[data-rules] [data-rule]').length === 0);
});
